import { useState } from 'react';
import {
  Copy, Check, BookOpen, Key, ShieldCheck, Globe, Package,
  Lock, ChevronDown, AlertTriangle,
} from 'lucide-react';
import { ProviderLayout } from './ProviderLayout';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Shared components ─────────────────────────────────────────────────────────

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group mt-1">
      <pre className="bg-slate-950 border border-slate-700/60 rounded-xl px-5 py-4 text-xs font-mono text-slate-300 overflow-x-auto leading-relaxed whitespace-pre">
        <code>{code}</code>
      </pre>
      <button
        onClick={copy}
        className="absolute top-3 right-3 p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-500 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors opacity-0 group-hover:opacity-100"
        title="Kopieren"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function MethodBadge({ method }) {
  const colors = {
    GET:    'bg-blue-500/15 text-blue-400 border-blue-500/30',
    POST:   'bg-green-500/15 text-green-400 border-green-500/30',
    PUT:    'bg-amber-500/15 text-amber-400 border-amber-500/30',
    DELETE: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <span className={`shrink-0 px-2.5 py-0.5 rounded text-xs font-bold border font-mono w-16 text-center inline-block ${colors[method]}`}>
      {method}
    </span>
  );
}

function StatusBadge({ code }) {
  const isSuccess = code >= 200 && code < 300;
  const cls = isSuccess
    ? 'bg-green-500/15 text-green-400 border-green-500/30'
    : 'bg-red-500/15 text-red-400 border-red-500/30';
  const labels = {
    200: '200 OK', 201: '201 Created', 204: '204 No Content',
    400: '400 Bad Request', 401: '401 Unauthorized',
    403: '403 Forbidden', 404: '404 Not Found', 422: '422 Unprocessable',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold border font-mono ${cls}`}>
      {labels[code] || `${code}`}
    </span>
  );
}

function Field({ name, type, required, desc, values }) {
  return (
    <tr className="border-b border-slate-800/60 last:border-0">
      <td className="py-2 pr-4 font-mono text-xs text-cyan-400 whitespace-nowrap align-top">{name}</td>
      <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap align-top">{type}</td>
      <td className="py-2 pr-4 text-xs align-top">
        {required
          ? <span className="text-orange-400">Pflicht</span>
          : <span className="text-slate-600">optional</span>}
      </td>
      <td className="py-2 text-xs text-slate-400 align-top">
        {desc}{values && <span className="text-slate-600 ml-1">({values})</span>}
      </td>
    </tr>
  );
}

function FieldTable({ children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 mt-1">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-800 bg-slate-900/40 text-xs text-slate-500 uppercase tracking-wider">
            <th className="text-left px-4 py-2">Feld</th>
            <th className="text-left px-4 py-2">Typ</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Beschreibung</th>
          </tr>
        </thead>
        <tbody className="px-4">{children}</tbody>
      </table>
    </div>
  );
}

// ── Accordion endpoint ─────────────────────────────────────────────────────────

function EndpointAccordion({
  method, path, summary, auth = true,
  requestCode, responseCode, responseStatus = 200,
  isDelete = false, children,
}) {
  const [open, setOpen] = useState(false);

  const borderColor = open ? 'border-slate-600' : 'border-slate-800/60';
  const headerBg   = open ? 'bg-slate-800/50' : 'bg-slate-900/30 hover:bg-slate-800/30';

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${borderColor}`}
      data-testid={`endpoint-${method.toLowerCase()}-${path.replace(/\//g, '-').replace(/[{}]/g, '').replace(/--+/g, '-')}`}
    >
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${headerBg}`}
      >
        <MethodBadge method={method} />
        <code className="flex-1 text-xs sm:text-sm font-mono text-slate-200 truncate">{path}</code>
        <span className="text-xs text-slate-500 hidden md:block truncate max-w-xs">{summary}</span>
        {!auth && (
          <span className="shrink-0 px-1.5 py-0.5 rounded text-xs border bg-slate-800/50 text-slate-500 border-slate-700">
            öffentlich
          </span>
        )}
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-slate-800 bg-slate-900/10 p-5 space-y-4">
          <p className="text-sm text-slate-400 leading-relaxed">{summary}</p>

          {/* Request + Response side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Request
              </p>
              <CodeBlock code={requestCode} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Response
                </p>
                <StatusBadge code={responseStatus} />
              </div>
              <CodeBlock code={responseCode} />
            </div>
          </div>

          {/* Optional extra content (schema table, etc.) */}
          {children}
        </div>
      )}
    </div>
  );
}

// ── Tab sections ──────────────────────────────────────────────────────────────

function AuthSection() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-400 leading-relaxed">
        Alle authentifizierten Endpunkte erfordern einen gültigen API-Schlüssel.
        Erstelle Schlüssel im Bereich{' '}
        <span className="text-cyan-400 font-medium">API-Schlüssel</span>.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-300 flex items-start gap-2.5">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Bewahre deinen API-Schlüssel sicher auf. Er wird <strong>nur einmal</strong> angezeigt
          und kann danach nicht wiederhergestellt werden. Schlüssel beginnen mit{' '}
          <code className="bg-amber-500/10 px-1 rounded">hcp_</code>.
        </span>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Header-Format</p>
        <CodeBlock code={`X-API-Key: hcp_xxxxxxxxxxxxxxxxxxxxxxxxxxxx`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
            Beispiel-Request
          </p>
          <CodeBlock code={`curl -X GET ${API_BASE}/provider/me \\\n  -H "X-API-Key: hcp_xxxxxx"`} />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            HTTP-Statuscodes
          </p>
          <div className="space-y-1.5 mt-1">
            {[
              { code: '200', color: 'text-green-400', desc: 'OK – Anfrage erfolgreich' },
              { code: '401', color: 'text-amber-400', desc: 'Nicht autorisiert – Schlüssel fehlt/ungültig' },
              { code: '403', color: 'text-orange-400', desc: 'Verboten – kein Zugriff auf diese Ressource' },
              { code: '404', color: 'text-red-400',   desc: 'Nicht gefunden' },
              { code: '422', color: 'text-red-400',   desc: 'Validierungsfehler – Pflichtfelder fehlen' },
            ].map(({ code, color, desc }) => (
              <div key={code} className="flex items-center gap-3 px-3 py-1.5 bg-slate-900/40 border border-slate-800 rounded-lg">
                <code className={`text-xs font-bold font-mono ${color} w-8`}>{code}</code>
                <span className="text-xs text-slate-400">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
          Beispiel: 401 Fehler-Response
        </p>
        <CodeBlock code={JSON.stringify({ detail: "Invalid or missing API key" }, null, 2)} />
      </div>
    </div>
  );
}

function ProfileSection() {
  const GET_RES = JSON.stringify({
    id: 1,
    name: "Mein Hosting GmbH",
    url: "https://mein-hosting.de",
    tagline: "Günstiges Hosting seit 2010",
    description: "Wir bieten zuverlässiges Hosting für jeden Bedarf.",
    logo_url: "https://mein-hosting.de/logo.png",
    founded_year: 2010,
    contact_email: "info@mein-hosting.de",
    webspace: 1, vserver: 1, dedi: 0, domains: 1,
    gameserver: 0, discordbots: 0, musicbots: 0,
    teamspeak: 0, licenses: 0,
  }, null, 2);

  const PUT_REQ = `curl -X PUT ${API_BASE}/provider/me \\
  -H "X-API-Key: hcp_xxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Mein Hosting GmbH",
    "url": "https://mein-hosting.de",
    "tagline": "Günstiges Hosting seit 2010",
    "description": "Wir bieten...",
    "logo_url": "https://mein-hosting.de/logo.png",
    "founded_year": 2010,
    "contact_email": "info@mein-hosting.de",
    "webspace": 1, "vserver": 1, "dedi": 0,
    "domains": 1, "gameserver": 0, "discordbots": 0,
    "musicbots": 0, "teamspeak": 0, "licenses": 0
  }'`;

  const PUT_RES = JSON.stringify({ message: "Profil erfolgreich aktualisiert" }, null, 2);

  return (
    <div className="space-y-3">
      <EndpointAccordion
        method="GET"
        path="/api/provider/me"
        summary="Aktuelles Anbieter-Profil abrufen – gibt alle Profildaten zurück."
        requestCode={`curl -X GET ${API_BASE}/provider/me \\\n  -H "X-API-Key: hcp_xxxxxx"`}
        responseCode={GET_RES}
        responseStatus={200}
      />
      <EndpointAccordion
        method="PUT"
        path="/api/provider/me"
        summary="Profil aktualisieren – alle Felder optional, nur gesendete Felder werden überschrieben."
        requestCode={PUT_REQ}
        responseCode={PUT_RES}
        responseStatus={200}
      >
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 mt-2">Schema</p>
          <FieldTable>
            <Field name="name"          type="string"  required desc="Anzeigename des Anbieters" />
            <Field name="url"           type="string"  desc="Website-URL" />
            <Field name="tagline"       type="string"  desc="Kurzbeschreibung / Slogan (1–2 Sätze)" />
            <Field name="description"   type="string"  desc="Ausführliche Beschreibung" />
            <Field name="logo_url"      type="string"  desc="URL zum Anbieter-Logo" />
            <Field name="founded_year"  type="integer" desc="Gründungsjahr" />
            <Field name="contact_email" type="string"  desc="Öffentliche Kontakt-E-Mail" />
            <Field name="webspace"      type="0 | 1"   desc="Bietet Webspace an" />
            <Field name="vserver"       type="0 | 1"   desc="Bietet VServer an" />
            <Field name="dedi"          type="0 | 1"   desc="Bietet Dedicated Server an" />
            <Field name="domains"       type="0 | 1"   desc="Bietet Domains an" />
            <Field name="gameserver"    type="0 | 1"   desc="Bietet Gameserver an" />
            <Field name="discordbots"   type="0 | 1"   desc="Bietet Discord-Bot-Hosting an" />
            <Field name="musicbots"     type="0 | 1"   desc="Bietet Musicbot-Hosting an" />
            <Field name="teamspeak"     type="0 | 1"   desc="Bietet TeamSpeak-Server an" />
            <Field name="licenses"      type="0 | 1"   desc="Bietet Lizenzen an" />
          </FieldTable>
        </div>
      </EndpointAccordion>
    </div>
  );
}

function ProductsSection() {
  const LIST_RES = JSON.stringify([{
    id: 42, hosting_id: 1,
    name: "VServer S",
    description: "Einstiegs-VServer mit SSD-Speicher",
    price: 4.99, price_period: "monthly",
    category: "vserver",
    url: "https://mein-hosting.de/vserver-s",
    active: 1, sort_order: 0,
    cpu_type: "AMD Epyc 7302P", cpu_cores: "2 vCore",
    ram_type: "DDR4 ECC", ram_size: "4 GB",
    storage_type: "NVMe SSD", storage_size: "40 GB",
    bandwidth: "1 Gbit/s", ddos_protection: "Voxility",
    traffic: "unlimitiert",
  }], null, 2);

  const CREATE_REQ = `curl -X POST ${API_BASE}/provider/products \\
  -H "X-API-Key: hcp_xxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "VServer S",
    "description": "Einstiegs-VServer",
    "price": 4.99,
    "price_period": "monthly",
    "category": "vserver",
    "url": "https://mein-hosting.de/vserver-s",
    "cpu_type": "AMD Epyc 7302P",
    "cpu_cores": "2 vCore",
    "ram_type": "DDR4 ECC",
    "ram_size": "4 GB",
    "storage_type": "NVMe SSD",
    "storage_size": "40 GB",
    "bandwidth": "1 Gbit/s",
    "ddos_protection": "Voxility",
    "traffic": "unlimitiert"
  }'`;

  const CREATE_RES = JSON.stringify({ id: 43, message: "Produkt erfolgreich erstellt" }, null, 2);

  const UPDATE_REQ = `curl -X PUT ${API_BASE}/provider/products/42 \\
  -H "X-API-Key: hcp_xxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "VServer S Pro",
    "price": 5.99,
    "ram_size": "8 GB"
  }'`;

  const UPDATE_RES = JSON.stringify({ message: "Produkt erfolgreich aktualisiert" }, null, 2);

  const DELETE_REQ = `curl -X DELETE ${API_BASE}/provider/products/42 \\
  -H "X-API-Key: hcp_xxxxxx"`;

  const DELETE_RES = JSON.stringify({ message: "Produkt erfolgreich gelöscht" }, null, 2);

  return (
    <div className="space-y-3">
      <EndpointAccordion
        method="GET"
        path="/api/provider/products"
        summary="Alle eigenen Produkte auflisten – gibt ein Array aller Produkte zurück."
        requestCode={`curl -X GET ${API_BASE}/provider/products \\\n  -H "X-API-Key: hcp_xxxxxx"`}
        responseCode={LIST_RES}
        responseStatus={200}
      />
      <EndpointAccordion
        method="POST"
        path="/api/provider/products"
        summary="Neues Produkt erstellen – gibt die neue ID zurück."
        requestCode={CREATE_REQ}
        responseCode={CREATE_RES}
        responseStatus={201}
      >
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 mt-2">Schema</p>
          <FieldTable>
            <Field name="name"            type="string"  required desc="Produktname" />
            <Field name="description"     type="string"  desc="Produktbeschreibung" />
            <Field name="price"           type="number"  desc="Preis in Euro (Standard: 0.0)" />
            <Field name="price_period"    type="string"  desc="Abrechnungszeitraum" values="monthly | yearly | one-time" />
            <Field name="category"        type="string"  desc="Kategorie" values="webspace | vserver | dedi | gameserver | teamspeak | discordbots | musicbots | domains | other" />
            <Field name="url"             type="string"  desc="Direktlink zum Produkt" />
            <Field name="active"          type="0 | 1"   desc="Sichtbarkeit (Standard: 1)" />
            <Field name="sort_order"      type="integer" desc="Sortierposition (Standard: 0)" />
            <Field name="cpu_type"        type="string"  desc="CPU-Modell (z.B. AMD Epyc 7302P)" />
            <Field name="cpu_cores"       type="string"  desc="Anzahl Kerne (z.B. 4 vCore)" />
            <Field name="ram_type"        type="string"  desc="RAM-Typ (z.B. DDR4 ECC)" />
            <Field name="ram_size"        type="string"  desc="RAM-Größe (z.B. 4 GB)" />
            <Field name="storage_type"    type="string"  desc="Speicher-Typ (z.B. NVMe SSD)" />
            <Field name="storage_size"    type="string"  desc="Speicher-Größe (z.B. 40 GB)" />
            <Field name="bandwidth"       type="string"  desc="Netzwerkanbindung (z.B. 1 Gbit/s)" />
            <Field name="ddos_protection" type="string"  desc="DDoS-Schutz-Anbieter/-Typ" />
            <Field name="traffic"         type="string"  desc="Inklusives Traffic-Volumen" />
          </FieldTable>
        </div>
      </EndpointAccordion>
      <EndpointAccordion
        method="PUT"
        path="/api/provider/products/{id}"
        summary="Bestehendes Produkt aktualisieren – nur gesendete Felder werden überschrieben."
        requestCode={UPDATE_REQ}
        responseCode={UPDATE_RES}
        responseStatus={200}
      />
      <EndpointAccordion
        method="DELETE"
        path="/api/provider/products/{id}"
        summary="Produkt dauerhaft löschen – diese Aktion kann nicht rückgängig gemacht werden."
        requestCode={DELETE_REQ}
        responseCode={DELETE_RES}
        responseStatus={200}
        isDelete
      />
    </div>
  );
}

function PublicSection() {
  const HOSTINGS_RES = JSON.stringify([{
    id: 1,
    name: "Mein Hosting GmbH",
    url: "https://mein-hosting.de",
    tagline: "Günstiges Hosting seit 2010",
    webspace: 1, vserver: 1, dedi: 0, domains: 1,
    gameserver: 0, discordbots: 0, musicbots: 0,
    teamspeak: 0, licenses: 0,
  }], null, 2);

  const DETAIL_RES = JSON.stringify({
    id: 1,
    name: "Mein Hosting GmbH",
    url: "https://mein-hosting.de",
    tagline: "Günstiges Hosting seit 2010",
    description: "Wir bieten zuverlässiges Hosting für jeden Bedarf.",
    logo_url: "https://mein-hosting.de/logo.png",
    founded_year: 2010,
    contact_email: "info@mein-hosting.de",
    webspace: 1, vserver: 1, dedi: 0, domains: 1,
    gameserver: 0, discordbots: 0, musicbots: 0,
    teamspeak: 0, licenses: 0,
  }, null, 2);

  const PRODUCTS_RES = JSON.stringify([{
    id: 42, hosting_id: 1,
    name: "VServer S",
    price: 4.99, price_period: "monthly",
    category: "vserver",
    url: "https://mein-hosting.de/vserver-s",
    active: 1,
    cpu_type: "AMD Epyc 7302P", cpu_cores: "2 vCore",
    ram_size: "4 GB", storage_size: "40 GB",
    bandwidth: "1 Gbit/s",
  }], null, 2);

  return (
    <div className="space-y-3">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl px-4 py-3 text-xs text-slate-400 flex items-start gap-2.5 mb-2">
        <Globe className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
        <span>
          Diese Endpunkte sind öffentlich zugänglich und benötigen <strong>keinen API-Schlüssel</strong>.
          Sie liefern dieselben Daten wie die Hauptseite.
        </span>
      </div>

      <EndpointAccordion
        method="GET"
        path="/api/hostings"
        summary="Alle Anbieter auflisten – gibt ein Array aller registrierten Hosting-Anbieter zurück."
        auth={false}
        requestCode={`curl -X GET ${API_BASE}/hostings`}
        responseCode={HOSTINGS_RES}
        responseStatus={200}
      />
      <EndpointAccordion
        method="GET"
        path="/api/hostings/{id}"
        summary="Einzelnen Anbieter abrufen – gibt das vollständige Profil anhand der ID zurück."
        auth={false}
        requestCode={`curl -X GET ${API_BASE}/hostings/1`}
        responseCode={DETAIL_RES}
        responseStatus={200}
      />
      <EndpointAccordion
        method="GET"
        path="/api/hostings/{id}/products"
        summary="Aktive Produkte eines Anbieters – gibt alle sichtbaren Produkte zurück."
        auth={false}
        requestCode={`curl -X GET ${API_BASE}/hostings/1/products`}
        responseCode={PRODUCTS_RES}
        responseStatus={200}
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const TABS = [
  { id: 'auth',     label: 'Authentifizierung', icon: Lock },
  { id: 'profile',  label: 'Profil',            icon: Key },
  { id: 'products', label: 'Produkte',           icon: Package },
  { id: 'public',   label: 'Öffentlich',         icon: Globe },
];

export function ProviderApiDocs() {
  const [tab, setTab] = useState('auth');

  return (
    <ProviderLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
            <BookOpen className="w-6 h-6 text-cyan-400" />
            API-Dokumentation
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            REST-API für den Zugriff auf dein Profil und deine Produkte.
            Basis-URL:{' '}
            <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300 text-xs">{API_BASE}</code>
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 border-b border-slate-800 mb-6 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`api-docs-tab-${id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                tab === id
                  ? 'text-cyan-400 border-cyan-400'
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6">
          {tab === 'auth'     && <AuthSection />}
          {tab === 'profile'  && <ProfileSection />}
          {tab === 'products' && <ProductsSection />}
          {tab === 'public'   && <PublicSection />}
        </div>

        {/* Footer note */}
        <div className="mt-5 px-4 py-3.5 bg-slate-800/30 border border-slate-700/50 rounded-xl text-xs text-slate-500 leading-relaxed">
          <span className="text-slate-400 font-medium">Hinweis:</span>{' '}
          Die API ist für den Zugriff auf dein eigenes Profil gedacht
          (max. ~60 Anfragen/Minute). Bei Fragen wende dich an unser Team im Discord.
        </div>
      </div>
    </ProviderLayout>
  );
}
